import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { AddUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Divider,
  Flex,
  Form,
  notification,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { produce } from 'immer';
import { debounce } from 'lodash';
import { usePrevious } from 'react-use';
import styled from 'styled-components';

import SectionCollapse from '../../common/SectionCollapse';
import {
  ExperimentAssignmentStrategy,
  ExpFormField,
} from '../../helper/consts';
import { generateMetricMessage } from '../../helper/metric';
import { getAssignByFacetFromDomain } from '../../Modals/EditExperimentModal';
import MetricRow from './MetricRow';
import { Metric, MetricWithDimensionFields } from './types';
import {
  BLACKLISTED_GUARDRAIL_METRICS,
  constructDimensionFields,
  getOptions,
  normalizeMetricDataFromServer,
} from './utils';
import {
  GRABX_SLACK_CHANNEL,
  HTTP_STATUS,
  MIDAS_METRICS_ENDPOINT,
} from '@/constants';
import Service from '@/lib/App';
import * as logger from '@/utils/logging';

const { Text } = Typography;

const errorLimitMsg = (type: string, n: number) => {
  return `Maximum limit of ${n} ${type} metrics!`;
};

const DEFAULT_DEEP_DIVE_ALL_DIMENSIONS = true;
const DEFAULT_ENABLE_TRIGGERED_ANALYSIS = false;

// check if should clear metrics when strategy changes
const isCompatibleStrategy = (
  strategy1: string,
  strategy2: string,
): boolean => {
  if (strategy1 === strategy2) return true;

  const choiceStrategies = [
    ExperimentAssignmentStrategy.UNIFORM_CHOICE,
    ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
  ];

  return (
    choiceStrategies.includes(strategy1) && choiceStrategies.includes(strategy2)
  );
};

interface MetricsProps {
  experimentName?: string;
  isViewMode?: boolean;
}

export interface MetricState {
  primary_metrics: MetricWithDimensionFields[];
  secondary_metrics: MetricWithDimensionFields[];
  guardrail_metrics: MetricWithDimensionFields[];
}

interface AnalysisConfig extends MetricState {
  deepdive_all_dimensions: boolean;
  enable_triggered_analysis: boolean;
}

function removeDefaultGuardrailMetrics(analysisConfig) {
  if (!analysisConfig?.guardrail_metrics) return [];

  const blacklistedGuardrailNames = BLACKLISTED_GUARDRAIL_METRICS.map(
    (metric) => metric.name,
  );

  return analysisConfig?.guardrail_metrics.filter(
    (metric: Metric) => !blacklistedGuardrailNames.includes(metric.name),
  );
}

interface MetricsGlossary {
  data: any[];
  errorMessage: string | null;
  is_dimension_filter_enabled?: boolean;
  is_deepdive_enabled?: boolean;
  is_triggered_analysis_enabled?: boolean;
}

const getInitialAnalysisConfig = (): AnalysisConfig => ({
  primary_metrics: [],
  secondary_metrics: [],
  guardrail_metrics: [],
  deepdive_all_dimensions: false,
  enable_triggered_analysis: false,
});

const Metrics: React.FC<MetricsProps> = ({
  experimentName,
  isViewMode = false,
}) => {
  const [analysisConfig, setAnalysisConfig] = useState<AnalysisConfig>(
    getInitialAnalysisConfig,
  );
  const [schemaVersion, setSchemaVersion] = useState<number>(0);
  // Store raw analysis config to preserve deepdive_dimensions data
  const [rawAnalysisConfig, setRawAnalysisConfig] = useState<any>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [deepdiveMessage, setDeepdiveMessage] = useState<ReactNode>(<></>);

  const form = Form.useFormInstance();

  const allSelectedMetricsArray = useMemo(
    () => [
      ...analysisConfig.primary_metrics,
      ...analysisConfig.secondary_metrics,
      ...analysisConfig.guardrail_metrics,
    ],
    [analysisConfig],
  );

  // Add validation error tracking
  const hasValidationErrors = useMemo(() => {
    // Check primary metric
    const hasPrimaryMetricError = analysisConfig.primary_metrics.some(
      (metric) => !metric.name,
    );

    // Check secondary metrics
    const hasSecondaryMetricError = analysisConfig.secondary_metrics.some(
      (metric) => !metric.name,
    );

    // Check guardrail metrics
    const hasGuardrailMetricError = analysisConfig.guardrail_metrics.some(
      (metric) => !metric.name,
    );

    // Check filter validation for all metrics
    const hasFilterError = allSelectedMetricsArray.some((metric) => {
      if (!metric.dimension_filters?.[0]?.dimension_filter_constraints)
        return false;

      return metric.dimension_filters[0].dimension_filter_constraints.some(
        (constraint) =>
          !constraint.dimension_name ||
          !constraint.operator ||
          !constraint.values ||
          (Array.isArray(constraint.values) && constraint.values.length === 0),
      );
    });

    return (
      hasPrimaryMetricError ||
      hasSecondaryMetricError ||
      hasGuardrailMetricError ||
      hasFilterError
    );
  }, [showValidation, allSelectedMetricsArray]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [metricsGlossary, setMetricsGlossary] = useState<MetricsGlossary>({
    data: [],
    errorMessage: null,
    is_dimension_filter_enabled: false,
    is_deepdive_enabled: false,
    is_triggered_analysis_enabled: false,
  });
  const filterEnabled = useMemo(
    () => metricsGlossary.is_dimension_filter_enabled,
    [metricsGlossary.is_dimension_filter_enabled],
  );
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    form.setFieldValue('analysis_v2', {
      analysisConfig,
      setShowValidation,
      hasValidationErrors,
    });
  }, [analysisConfig, hasValidationErrors]);

  useEffect(() => {
    form.setFieldValue('schemaVersion', schemaVersion);
  }, [schemaVersion]);

  useEffect(() => {
    if (!experimentName) return;
    Service.planner
      .fetchAnalysisConfig(experimentName)
      .then((results) => {
        const { analysisConfig: config } = results.AnalysisV2;

        // Store raw config to preserve deepdive_dimensions data
        setRawAnalysisConfig(config);

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const guardrail_metrics = removeDefaultGuardrailMetrics(config);

        const normalizeMetrics = (
          metrics: MetricWithDimensionFields[] | null,
        ) => {
          return (
            metrics?.map(normalizeMetricDataFromServer)?.filter(Boolean) || []
          );
        };

        setAnalysisConfig({
          primary_metrics: normalizeMetrics(config?.primary_metrics),
          secondary_metrics: normalizeMetrics(config?.secondary_metrics),
          guardrail_metrics: normalizeMetrics(guardrail_metrics),
          deepdive_all_dimensions: config?.deepdive_all_dimensions || false,
          enable_triggered_analysis: config?.enable_triggered_analysis || false,
        });
        setSchemaVersion(results.AnalysisV2.schemaVersion);
      })
      .catch((_e) => {});
  }, [experimentName]);

  const domain = Form.useWatch(ExpFormField.DOMAIN, {
    form,
    preserve: true,
  });
  const strategyName = Form.useWatch(ExpFormField.STRATEGY.ROOT, {
    form,
    preserve: true,
  })?.name;
  const assignByFacet = getAssignByFacetFromDomain(domain)?.name;

  const previousStrategyName = usePrevious(strategyName);
  const previousAssignByFacet = usePrevious(assignByFacet);

  async function fetchMetricsGlossary(params: { [key: string]: string }) {
    setIsLoading(true);

    try {
      const res = await Service.planner.fetchMetricsGlossary(params);

      const isDimensionFilterEnabled =
        res?.data?.is_dimension_filter_enabled || false;
      const isDeepDiveEnabled = res?.data?.is_deepdive_enabled || false;
      const isTriggeredAnalysisEnabled =
        res?.data?.is_triggered_analysis_enabled || false;

      setMetricsGlossary({
        data: res?.data?.metrics || [],
        errorMessage: null,
        is_dimension_filter_enabled: isDimensionFilterEnabled,
        is_deepdive_enabled: isDeepDiveEnabled,
        is_triggered_analysis_enabled: isTriggeredAnalysisEnabled,
      });

      if (!isViewMode) {
        // set the deepdive_all_dimensions default value to true if deepdive is enabled
        setAnalysisConfig((prev) => ({
          ...prev,
          deepdive_all_dimensions: isDeepDiveEnabled
            ? DEFAULT_DEEP_DIVE_ALL_DIMENSIONS
            : false,
          enable_triggered_analysis: isTriggeredAnalysisEnabled
            ? DEFAULT_ENABLE_TRIGGERED_ANALYSIS
            : false,
        }));
      }
    } catch (e) {
      if (
        e?.response?.status === HTTP_STATUS.METHOD_NOT_ALLOWED &&
        e.response.data?.devMessage
      ) {
        setMetricsGlossary({
          data: [],
          errorMessage: e.response.data.devMessage,
          is_dimension_filter_enabled: false,
          is_deepdive_enabled: false,
          is_triggered_analysis_enabled: false,
        });
        return;
      }

      setMetricsGlossary({
        data: [],
        errorMessage: null,
        is_dimension_filter_enabled: false,
        is_deepdive_enabled: false,
        is_triggered_analysis_enabled: false,
      });
      notification.error({
        message: 'Failed to fetch metrics glossary',
        description: 'Please try again later.',
      });
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  }

  useEffect(() => {
    const cleanup = () => {
      setMetricsGlossary({
        data: [],
        errorMessage: null,
        is_dimension_filter_enabled: false,
        is_deepdive_enabled: false,
        is_triggered_analysis_enabled: false,
      });
    };

    // In view mode, skip MIDAS API calls and only use analysis API data
    if (isViewMode) {
      setMetricsGlossary({
        data: [],
        errorMessage: null,
        is_dimension_filter_enabled: false,
        is_deepdive_enabled: true,
        is_triggered_analysis_enabled: false,
      });
      setIsLoading(false);
      setIsInitializing(false);
      return;
    }

    // Handle strategy and facet changes with metric compatibility checking
    const handleStrategyChange = async () => {
      if (previousAssignByFacet !== assignByFacet) {
        setAnalysisConfig(getInitialAnalysisConfig);
        return;
      }

      if (previousStrategyName !== strategyName) {
        if (isCompatibleStrategy(previousStrategyName, strategyName)) {
          return;
        }

        const hasMetrics =
          analysisConfig.primary_metrics.some((m) => m.name) ||
          analysisConfig.secondary_metrics.some((m) => m.name) ||
          analysisConfig.guardrail_metrics.some((m) => m.name);

        if (!hasMetrics) {
          return;
        }

        try {
          const [primaryResults, secondaryResults, guardrailResults] =
            await Promise.all([
              filterCompatibleMetrics(
                analysisConfig.primary_metrics,
                assignByFacet,
                strategyName,
              ),
              filterCompatibleMetrics(
                analysisConfig.secondary_metrics,
                assignByFacet,
                strategyName,
              ),
              filterCompatibleMetrics(
                analysisConfig.guardrail_metrics,
                assignByFacet,
                strategyName,
              ),
            ]);

          setAnalysisConfig((prev) => ({
            ...prev,
            primary_metrics: primaryResults.compatible,
            secondary_metrics: secondaryResults.compatible,
            guardrail_metrics: guardrailResults.compatible,
          }));

          const totalIncompatible = [
            ...primaryResults.incompatible,
            ...secondaryResults.incompatible,
            ...guardrailResults.incompatible,
          ].filter((m) => m.name).length;

          const totalCompatible = [
            ...primaryResults.compatible,
            ...secondaryResults.compatible,
            ...guardrailResults.compatible,
          ].filter((m) => m.name).length;

          if (totalIncompatible > 0 && totalCompatible > 0) {
            notification.info({
              message: 'Assignment Strategy Changed',
              description: `${totalIncompatible} metric(s) removed due to incompatibility. ${totalCompatible} compatible metric(s) preserved.`,
              duration: 5,
            });
          } else if (totalIncompatible > 0 && totalCompatible === 0) {
            notification.warning({
              message: 'Assignment Strategy Changed',
              description:
                'All metrics were removed as they are not compatible with the new assignment strategy.',
              duration: 5,
            });
          } else if (totalIncompatible === 0 && totalCompatible > 0) {
            notification.success({
              message: 'Assignment Strategy Changed',
              description:
                'All existing metrics are compatible with the new assignment strategy.',
              duration: 3,
            });
          }
        } catch (error) {
          logger.error(
            error,
            'Error checking metric compatibility during strategy change',
            {
              previousStrategy: previousStrategyName,
              newStrategy: strategyName,
              assignByFacet,
            },
          );
        }
      }
    };

    handleStrategyChange();

    if (!assignByFacet || !strategyName) {
      return cleanup;
    }

    let params = {
      assign_by: assignByFacet,
      strategy: strategyName,
    };

    if (searchValue != '') {
      params = Object.assign(params, {
        owner: searchValue,
        description: searchValue,
        display_name: searchValue,
      });
    }

    fetchMetricsGlossary(params);
  }, [assignByFacet, searchValue, strategyName, isViewMode]);

  useEffect(() => {
    if (!analysisConfig.deepdive_all_dimensions) {
      setDeepdiveMessage(<></>);
      return;
    }

    const generateMessage = async () => {
      const nonEmptyMetrics = allSelectedMetricsArray.filter((m) => !!m.name);
      if (
        nonEmptyMetrics.length > 0 &&
        analysisConfig.deepdive_all_dimensions
      ) {
        const message = await generateMetricMessage(
          analysisConfig.deepdive_all_dimensions,
          strategyName,
          analysisConfig,
          isViewMode,
          rawAnalysisConfig,
          assignByFacet,
        );
        setDeepdiveMessage(message);
      } else {
        // Reset the message when there are no metrics
        setDeepdiveMessage(<></>);
      }
    };

    generateMessage();
  }, [
    allSelectedMetricsArray,
    analysisConfig.deepdive_all_dimensions,
    strategyName,
    isViewMode,
    rawAnalysisConfig,
  ]);

  const handleAddPrimaryMetric = () => {
    setAnalysisConfig((prev) =>
      produce(prev, (draft) => {
        draft.primary_metrics.push(
          constructDimensionFields({
            name: null,
          }),
        );
      }),
    );
  };

  const handleAddSecondaryMetric = () => {
    const secondaryMetrics = analysisConfig.secondary_metrics;

    const unpopulatedMetricRowExists = secondaryMetrics.some(
      (m) => m.name === null,
    );

    if (unpopulatedMetricRowExists) {
      return;
    }

    setAnalysisConfig((prev) =>
      produce(prev, (draft) => {
        draft.secondary_metrics.push(
          constructDimensionFields({
            name: null,
          }),
        );
      }),
    );
  };

  const handleAddGuardrailMetric = () => {
    const guardrailMetrics = analysisConfig.guardrail_metrics;

    const unpopulatedMetricRowExists = guardrailMetrics.some(
      (m) => m.name === null,
    );

    if (unpopulatedMetricRowExists) {
      return;
    }

    setAnalysisConfig((prev) =>
      produce(prev, (draft) => {
        draft.guardrail_metrics.push(
          constructDimensionFields({
            name: null,
          }),
        );
      }),
    );
  };

  const handlePrimaryMetricsDeletion = () => {
    setAnalysisConfig((prev) =>
      produce(prev, (draft) => {
        draft.primary_metrics = [];
      }),
    );
  };

  const handleSecondaryMetricsDeletion = (index: number) => {
    const filteredMetrics = analysisConfig.secondary_metrics.filter(
      (_, i) => i !== index,
    );
    setAnalysisConfig((prev) =>
      produce(prev, (draft) => {
        draft.secondary_metrics = filteredMetrics;
      }),
    );
  };

  const handleGuardrailMetricsDeletion = (index: number) => {
    const filteredMetrics = analysisConfig.guardrail_metrics.filter(
      (_, i) => i !== index,
    );
    setAnalysisConfig((prev) =>
      produce(prev, (draft) => {
        draft.guardrail_metrics = filteredMetrics;
      }),
    );
  };

  const collapseProps = {
    title: (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography.Text>Metrics</Typography.Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
            }}
          >
            <Typography.Metadata style={{ color: '#707070' }}>
              Select metrics to measure the impact of your experiment.
            </Typography.Metadata>

            <Typography.Link
              href={MIDAS_METRICS_ENDPOINT}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 12,
              }}
            >
              Explore Midas to discover available metrics.
            </Typography.Link>
          </div>
        </div>
      </div>
    ),
    // title: 'Metrics',
    defaultOpen: true,
  };

  if (!isViewMode && !!metricsGlossary.errorMessage) {
    return (
      <SectionCollapse {...collapseProps}>
        <MetricsUnsupported errorMessage={metricsGlossary.errorMessage} />
      </SectionCollapse>
    );
  }

  if (
    isViewMode &&
    !analysisConfig?.primary_metrics?.length &&
    !analysisConfig?.secondary_metrics?.length &&
    !analysisConfig?.guardrail_metrics?.length &&
    !metricsGlossary.data.length &&
    !isLoading &&
    !searchValue
  ) {
    return (
      <SectionCollapse {...collapseProps}>
        <MetricsWrapper>
          <img
            src="/file.svg"
            width={130}
            height={60}
            style={{ marginTop: 40, marginBottom: 40 }}
          />
          <Text strong style={{ margin: 0, fontSize: 24 }}>
            No metrics available!
          </Text>
          <Text style={{ color: '#707070' }}>
            This experiment does not have any metrics yet
          </Text>
        </MetricsWrapper>
      </SectionCollapse>
    );
  }

  return (
    <SectionCollapse {...collapseProps}>
      <MetricsWrapper className="metrics-wrapper">
        <Text strong>Primary Metric (P0)</Text>
        {isViewMode && !analysisConfig?.primary_metrics?.length ? (
          <div>No selected metrics</div>
        ) : (
          <>
            <MetricRow
              filterEnabled={filterEnabled}
              strategy={strategyName}
              metricsGlossaryData={metricsGlossary.data || []}
              metrics={analysisConfig}
              setMetrics={setAnalysisConfig}
              metricType="primary"
              metricIndex={0}
              options={getOptions(
                metricsGlossary.data,
                analysisConfig.primary_metrics,
              )}
              onSearch={debounce((e) => {
                setSearchValue(e);
              }, 500)}
              isViewMode={isViewMode}
              rawAnalysisConfig={rawAnalysisConfig}
              onDelete={handlePrimaryMetricsDeletion}
              showValidation={showValidation}
              prefix="P0"
            />
            {showValidation &&
              analysisConfig.primary_metrics.some((metric) => !metric.name) && (
                <Typography.Text type="danger">
                  Please select a metric
                </Typography.Text>
              )}
          </>
        )}
        {!isViewMode && !analysisConfig?.primary_metrics?.length && (
          <Button
            style={{
              minWidth: '100%',
              minHeight: 32,
              background: '#F5F5F5',
              border: 'none',
            }}
            icon={<AddUniversal />}
            onClick={handleAddPrimaryMetric}
          />
        )}
      </MetricsWrapper>

      <MetricsWrapper>
        <Text strong>Secondary Metrics (P1)</Text>
        {isViewMode && !analysisConfig.secondary_metrics.length ? (
          <div>No selected metrics</div>
        ) : (
          analysisConfig.secondary_metrics.map((metric, index) => (
            <>
              <MetricRow
                filterEnabled={filterEnabled}
                strategy={strategyName}
                key={`secondary-${metric?.name}-${index}`}
                metricsGlossaryData={metricsGlossary.data || []}
                metrics={analysisConfig}
                metricType="secondary"
                setMetrics={setAnalysisConfig}
                metricIndex={index}
                options={getOptions(
                  metricsGlossary.data,
                  analysisConfig.secondary_metrics,
                )}
                onSearch={setSearchValue}
                isViewMode={isViewMode}
                rawAnalysisConfig={rawAnalysisConfig}
                onDelete={() => handleSecondaryMetricsDeletion(index)}
                showValidation={showValidation}
                prefix="P1"
              />
              {showValidation && !metric.name && (
                <Typography.Text type="danger">
                  Please select a metric
                </Typography.Text>
              )}
            </>
          ))
        )}
        {!isViewMode &&
          (analysisConfig.secondary_metrics.length >= 5 ? (
            <Tooltip title={errorLimitMsg('Secondary', 5)}>
              <Button
                style={{
                  minWidth: '100%',
                  minHeight: 32,
                  background: '#F5F5F5',
                  border: 'none',
                }}
                icon={<AddUniversal />}
                disabled
              />
            </Tooltip>
          ) : (
            <Button
              style={{
                minWidth: '100%',
                minHeight: 32,
                background: '#F5F5F5',
                border: 'none',
              }}
              icon={<AddUniversal />}
              onClick={handleAddSecondaryMetric}
            />
          ))}
      </MetricsWrapper>

      <MetricsWrapper style={{ paddingBottom: 0 }}>
        <Text strong>Guardrail Metrics (G)</Text>
        {isViewMode && !analysisConfig.guardrail_metrics.length ? (
          <div>No selected metrics</div>
        ) : (
          analysisConfig.guardrail_metrics.map((metric, index) => (
            <>
              <MetricRow
                filterEnabled={filterEnabled}
                strategy={strategyName}
                key={`guardrail-${metric?.name}-${index}`}
                metricsGlossaryData={metricsGlossary.data || []}
                metrics={analysisConfig}
                metricType="guardrail"
                setMetrics={setAnalysisConfig}
                metricIndex={index}
                options={getOptions(
                  metricsGlossary.data,
                  analysisConfig.guardrail_metrics,
                )}
                onSearch={setSearchValue}
                isViewMode={isViewMode}
                rawAnalysisConfig={rawAnalysisConfig}
                onDelete={() => handleGuardrailMetricsDeletion(index)}
                showValidation={showValidation}
                prefix="G"
              />
              {showValidation && !metric.name && (
                <Typography.Text type="danger">
                  Please select a metric
                </Typography.Text>
              )}
            </>
          ))
        )}
        {!isViewMode &&
          (analysisConfig.guardrail_metrics.length >= 2 ? (
            <Tooltip title={errorLimitMsg('Guardrail', 2)}>
              <Button
                style={{
                  minWidth: '100%',
                  minHeight: 32,
                  background: '#F5F5F5',
                  border: 'none',
                }}
                icon={<AddUniversal />}
                disabled
              />
            </Tooltip>
          ) : (
            <Button
              style={{
                minWidth: '100%',
                minHeight: 32,
                background: '#F5F5F5',
                border: 'none',
              }}
              icon={<AddUniversal />}
              onClick={handleAddGuardrailMetric}
            />
          ))}
      </MetricsWrapper>

      {isInitializing ? (
        <Flex justify="center" align="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : (
        <>
          {(metricsGlossary.is_deepdive_enabled ||
            metricsGlossary.is_triggered_analysis_enabled) && <Divider />}
          <Flex vertical gap={16}>
            {metricsGlossary.is_deepdive_enabled && (
              <Alert
                style={{ borderColor: '#dbdbdb' }}
                message="Enable Deep Dive"
                description={
                  analysisConfig.deepdive_all_dimensions ? (
                    deepdiveMessage
                  ) : (
                    <></>
                  )
                }
                type="neutral"
                action={
                  <Switch
                    checked={analysisConfig.deepdive_all_dimensions}
                    onChange={(checked) => {
                      setAnalysisConfig((prev) => ({
                        ...prev,
                        deepdive_all_dimensions: checked,
                      }));
                    }}
                    disabled={isViewMode}
                  />
                }
              />
            )}

            {metricsGlossary.is_triggered_analysis_enabled && (
              <Alert
                style={{ borderColor: '#dbdbdb' }}
                message="Trigger Analysis"
                description="Enable this to analyse results only for users who triggered your experiment variable."
                type="neutral"
                action={
                  <Switch
                    checked={analysisConfig.enable_triggered_analysis}
                    onChange={(checked) => {
                      setAnalysisConfig((prev) => ({
                        ...prev,
                        enable_triggered_analysis: checked,
                      }));
                    }}
                    disabled={isViewMode}
                  />
                }
              />
            )}
          </Flex>
        </>
      )}
    </SectionCollapse>
  );
};

export function MetricsUnsupported({ errorMessage }: { errorMessage: string }) {
  return (
    <MetricsWrapper>
      <img
        src="/file.svg"
        width={130}
        height={60}
        style={{ marginTop: 40, marginBottom: 40 }}
      />
      <Text strong style={{ margin: 0, fontSize: 24 }}>
        Metrics are not supported!
      </Text>
      <Text style={{ color: '#707070' }}>
        {errorMessage}
        <br />
        For more information, please contact us and submit a feature request.
      </Text>
      <div style={{ paddingTop: 20 }}>
        <Button
          onClick={(_) => {
            window.open(GRABX_SLACK_CHANNEL);
          }}
          type="primary"
          size="small"
          style={{ width: 222, marginRight: 8 }}
        >
          Connect to the GrabX team
        </Button>
        <Button
          onClick={(_e) => {
            window.open(GRABX_SLACK_CHANNEL);
          }}
          type="secondary"
          size="small"
          style={{ width: 212 }}
        >
          Submit a feature request
        </Button>
      </div>
    </MetricsWrapper>
  );
}

export const MetricTag = styled(Tag)`
  border-radius: 8px;
  border: none;
  background-color: #f5f5f5;
  color: #3d3d3d;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  min-height: 40px;
  min-width: 40px;
  align-items: center;
  justify-content: center;
`;

const MetricsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  gap: 4px;
`;

// Helper functions for assignment strategy compatibility checking
async function checkMetricCompatibility(
  metricName: string,
  assignByFacet: string,
  newStrategy: string,
): Promise<boolean> {
  try {
    const params = {
      assign_by: assignByFacet,
      strategy: newStrategy,
    };

    const res = await Service.planner.fetchMetricsGlossary(params);
    const availableMetrics = res?.data?.metrics || [];

    // Check if the metric exists in the available metrics for the new strategy
    return availableMetrics.some((metric: any) => metric.name === metricName);
  } catch (error) {
    logger.error(error, 'Error checking metric compatibility with strategy', {
      metricName,
      assignByFacet,
      newStrategy,
    });
    return false;
  }
}

async function filterCompatibleMetrics(
  metrics: MetricWithDimensionFields[],
  assignByFacet: string,
  newStrategy: string,
): Promise<{
  compatible: MetricWithDimensionFields[];
  incompatible: MetricWithDimensionFields[];
}> {
  const compatible: MetricWithDimensionFields[] = [];
  const incompatible: MetricWithDimensionFields[] = [];

  for (const metric of metrics) {
    if (!metric.name) {
      // Keep empty metric slots
      compatible.push(metric);
      continue;
    }

    const isCompatible = await checkMetricCompatibility(
      metric.name,
      assignByFacet,
      newStrategy,
    );

    if (isCompatible) {
      compatible.push(metric);
    } else {
      incompatible.push(metric);
    }
  }

  return { compatible, incompatible };
}

export default Metrics;
